import { cssInterop } from 'nativewind'
import React from 'react'
import { Platform, SectionList, Text, View, useWindowDimensions } from 'react-native'

import NoResults from './NoResults'
import { searchableList } from './styles'
import type { OptionListProps } from './types'
import { toSectionListData } from './utils'

cssInterop(SectionList, { className: { target: 'style' } })

const MAX_HEIGHT_RATIO = 0.7

// Web lists are short (tens of rows), so web renders every row and never unmounts it:
// Ctrl+F, server rendering and e2e selectors by option text keep seeing all options.
const PLATFORM_LIST_PROPS = Platform.select({
  web: { initialNumToRender: Number.MAX_SAFE_INTEGER },
  default: {},
})

// Native caps the list at 70% of the window. Web uses the `web:max-h-[70vh]` class
// instead, so server-rendered HTML does not depend on the window size.
const useMaxHeightStyle = () => {
  const { height } = useWindowDimensions()
  return Platform.OS === 'web' ? undefined : { maxHeight: height * MAX_HEIGHT_RATIO }
}

const OptionList = (props: OptionListProps) => {
  const styles = searchableList({ isGrouped: props.isGrouped })

  return (
    <SectionList
      {...PLATFORM_LIST_PROPS}
      className={styles.scrollContainer()}
      style={useMaxHeightStyle()}
      sections={toSectionListData(props)}
      keyExtractor={(option) => String(option.value)}
      renderItem={({ item, index, section }) => props.renderOption(item, index, section.data)}
      renderSectionHeader={({ section }) =>
        section.title === undefined ? null : (
          <View className={styles.sectionHeader()}>
            <Text className={styles.sectionHeaderText()}>{section.title}</Text>
          </View>
        )
      }
      ListEmptyComponent={
        <NoResults
          labels={props.labels}
          emptyOptions={props.emptyOptions}
          renderOption={props.renderOption}
        />
      }
      stickySectionHeadersEnabled={false}
      keyboardShouldPersistTaps="handled"
      nestedScrollEnabled
      showsVerticalScrollIndicator
      role={props.isMultiple ? 'group' : 'radiogroup'}
      aria-label={props.title}
    />
  )
}

export default OptionList
